import React, { useEffect } from 'react';
import { Layout, Menu, Typography, Button, Space } from 'antd';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useIsRTL } from '../../utils/rtl';
import LanguageSwitcher from './LanguageSwitcher';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ApiOutlined,
  WalletOutlined,
  TransactionOutlined,
  SettingOutlined,
  LogoutOutlined,
  BugOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';

// Import pages
import DashboardPage from '../../pages/DashboardPage';
import RPCManagementPage from '../../pages/RPCManagementPageNew';
import WalletManagementPage from '../../pages/WalletManagementPage';
import TransactionsPage from '../../pages/TransactionsPage';
import TransactionDetailsPage from '../../pages/TransactionDetailsPage';
import WithdrawalMonitorPage from '../../pages/WithdrawalMonitorPage';
import DepositMonitorPage from '../../pages/DepositMonitorPage';
import SettingsPage from '../../pages/SettingsPage';
import CoinsManagementPage from '../../pages/CoinsManagementPage';
import NetworksManagementPage from '../../pages/NetworksManagementPage';
import CoinNetworksManagementPage from '../../pages/CoinNetworksManagementPage';
import QueueManagementPage from '../../pages/QueueManagementPage';
import CircuitBreakerManagementPage from '../../pages/CircuitBreakerManagementPage';
import IndexerManagementPage from '../../pages/IndexerManagementPage';
import DebugPage from '../../pages/DebugPage';
import TatumWebhookLogsPage from '../../pages/TatumWebhookLogsPage';

const { Header, Sider, Content, Footer } = Layout;
const { Title } = Typography;

// RTL-aware styled components
const StyledLayout = styled(Layout) <{ $isRTL: boolean }>`
  min-height: 100vh;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const StyledHeader = styled(Header)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #fff;
`;

const Logo = styled.div`
  padding: 16px;
  text-align: center;
  h4 {
    color: white;
    margin: 0;
  }
`;

const AppLayout: React.FC = () => {
  const { t, i18n } = useTranslationWithNamespace('app');
  const isRTL = useIsRTL();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isLoading: isLogoutLoading } = useAuth();

  // Set document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: t('navigation.dashboard', 'Dashboard'),
      onClick: () => navigate('/')
    },
    {
      key: '/rpc',
      icon: <ApiOutlined />,
      label: t('navigation.rpc', 'RPC Management'),
      onClick: () => navigate('/rpc')
    },
    {
      key: '/coins',
      icon: <ApiOutlined />,
      label: t('navigation.coins', 'Coins Management'),
      onClick: () => navigate('/coins')
    },
    {
      key: '/networks',
      icon: <ApiOutlined />,
      label: t('navigation.networks', 'Networks Management'),
      onClick: () => navigate('/networks')
    },
    {
      key: '/coin-networks',
      icon: <ApiOutlined />,
      label: t('navigation.coinNetworks', 'Coin Networks'),
      onClick: () => navigate('/coin-networks')
    },
    {
      key: '/wallet',
      icon: <WalletOutlined />,
      label: t('navigation.wallet', 'Wallet Management'),
      onClick: () => navigate('/wallet')
    },
    {
      key: '/transactions',
      icon: <TransactionOutlined />,
      label: t('navigation.transactions', 'Transactions'),
      onClick: () => navigate('/transactions')
    },
    {
      key: '/withdrawal-monitor',
      icon: <TransactionOutlined />,
      label: t('navigation.withdrawalMonitor', 'Withdrawal Monitor'),
      onClick: () => navigate('/withdrawal-monitor')
    },
    {
      key: '/deposit-monitor',
      icon: <TransactionOutlined />,
      label: t('navigation.depositMonitor', 'Deposit Monitor'),
      onClick: () => navigate('/deposit-monitor')
    },
    {
      key: '/tatum-webhooks',
      icon: <ApiOutlined />,
      label: t('navigation.tatumWebhooks', 'Tatum Webhooks'),
      onClick: () => navigate('/tatum-webhooks')
    },
    {
      key: '/indexers',
      icon: <ApiOutlined />,
      label: t('navigation.indexers', 'Indexer Management'),
      onClick: () => navigate('/indexers')
    },
    {
      key: '/queues',
      icon: <ApiOutlined />,
      label: t('navigation.queues', 'Queue Management'),
      onClick: () => navigate('/queues')
    },
    {
      key: '/circuit-breakers',
      icon: <SettingOutlined />,
      label: t('navigation.circuitBreakers', 'Circuit Breakers'),
      onClick: () => navigate('/circuit-breakers')
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('navigation.settings', 'Settings'),
      onClick: () => navigate('/settings')
    },
    {
      key: '/debug',
      icon: <BugOutlined />,
      label: t('navigation.debug', 'Debug'),
      onClick: () => navigate('/debug')
    }
  ];

  return (
    <StyledLayout $isRTL={isRTL}>
      <Sider width={250} theme="dark" breakpoint="lg" collapsedWidth="0">
        <Logo>
          <Title level={4} style={{ color: 'white', margin: 0 }}>
            {t('name', 'BitAzar')}
          </Title>
        </Logo>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <StyledHeader>
          <Title level={4} style={{ margin: 0 }}>
            {t('tagline', 'Wallet Engine Dashboard')}
          </Title>
          <Space>
            <LanguageSwitcher />
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={logout}
              loading={isLogoutLoading}
            >
              {t('auth:logout', 'Logout')}
            </Button>
          </Space>
        </StyledHeader>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/rpc/*" element={<RPCManagementPage />} />
            <Route path="/coins/*" element={<CoinsManagementPage />} />
            <Route path="/networks/*" element={<NetworksManagementPage />} />
            <Route path="/coin-networks/*" element={<CoinNetworksManagementPage />} />
            <Route path="/wallet/*" element={<WalletManagementPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/transactions/details/:id" element={<TransactionDetailsPage />} />
            <Route path="/withdrawal-monitor" element={<WithdrawalMonitorPage />} />
            <Route path="/deposit-monitor" element={<DepositMonitorPage />} />
            <Route path="/indexers/*" element={<IndexerManagementPage />} />
            <Route path="/queues/*" element={<QueueManagementPage />} />
            <Route path="/circuit-breakers/*" element={<CircuitBreakerManagementPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="/debug/*" element={<DebugPage />} />
            <Route path="/tatum-webhooks" element={<TatumWebhookLogsPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        </Content>
        <Footer style={{ textAlign: 'center' }}>
          BitAzar Wallet Engine ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </StyledLayout>
  );
};

export default AppLayout;
